import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar,
  Users,
  BookOpen,
  Check,
  X,
  Clock,
  Minus,
  FileText,
  Search,
  Save,
  RefreshCw,
  Filter,
  CheckCircle,
  AlertCircle,
  ChevronDown,
  UserCheck,
  UserX,
} from "lucide-react";
import axios from "axios";
import { server } from "../server";
import { toast } from "react-toastify";
import useAuth from "../useAuth";

const AttendancePage = () => {
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [batches, setBatches] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [attendance, setAttendance] = useState({});

  const attendanceStatuses = [
    {
      value: "present",
      label: "Present",
      icon: Check,
      color: "text-emerald-400",
      bg: "bg-emerald-500/20",
      border: "border-emerald-500/50",
      activeBg: "bg-emerald-500",
    },
    {
      value: "absent",
      label: "Absent",
      icon: X,
      color: "text-rose-400",
      bg: "bg-rose-500/20",
      border: "border-rose-500/50",
      activeBg: "bg-rose-500",
    },
    {
      value: "late",
      label: "Late",
      icon: Clock,
      color: "text-amber-400",
      bg: "bg-amber-500/20",
      border: "border-amber-500/50",
      activeBg: "bg-amber-500",
    },
    {
      value: "half_day",
      label: "Half Day",
      icon: Minus,
      color: "text-blue-400",
      bg: "bg-blue-500/20",
      border: "border-blue-500/50",
      activeBg: "bg-blue-500",
    },
    {
      value: "leave",
      label: "Leave",
      icon: FileText,
      color: "text-purple-400",
      bg: "bg-purple-500/20",
      border: "border-purple-500/50",
      activeBg: "bg-purple-500",
    },
  ];

  useEffect(() => {
    fetchBatches();
    fetchSubjects();
  }, []);

  useEffect(() => {
    if (selectedBatch && selectedDate) {
      fetchStudents();
    } else {
      setStudents([]);
      setAttendance({});
    }
  }, [selectedBatch, selectedDate]);

  const fetchBatches = async () => {
    try {
      const { data } = await axios.get(`${server}/batch/all`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      if (data.success && data.data) {
        setBatches(data.data);
      }
    } catch (error) {
      toast.error("Failed to fetch batches");
    }
  };

  const fetchSubjects = async () => {
    try {
      const { data } = await axios.get(`${server}/subject/all`, {
        params: { pageSize: 100 },
      });
      setSubjects(data.data || []);
    } catch (error) {
      toast.error("Failed to fetch subjects");
    }
  };

  const fetchStudents = async () => {
    try {
      setLoading(true);
      // Fetch all students (can optimize to fetch by batch if API supported it, filtering client side for now as requested)
      const studentsRes = await axios.get(`${server}/student/all`, {
        params: { pageSize: 1000 },
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      // Fetch existing attendance for this batch and date
      const attendanceRes = await axios.get(`${server}/attendance/all`, {
        params: {
          batchId: selectedBatch,
          fromDate: new Date(selectedDate).toISOString(),
          toDate: new Date(selectedDate).toISOString(),
          pageSize: 1000,
        },
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      const allStudents = studentsRes.data.data || [];
      // Ideally we should filter students by batch here if we had that mapping in the student object.
      // For now, assuming the user will select valid students or all students are available.
      const batchStudents = allStudents;

      setStudents(batchStudents);

      const existingAttendance = attendanceRes.data.data || [];

      // Map existing attendance
      const initialAttendance = {};
      batchStudents.forEach((student) => {
        const record = existingAttendance.find(
          (a) => a.studentId === student.id
        );
        initialAttendance[student.id] = {
          status: record ? record.status : "present", // Default to present
          remarks: record ? record.remarks : "",
        };
      });
      setAttendance(initialAttendance);
    } catch (error) {
      toast.error("Failed to fetch data");
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (studentId, status) => {
    setAttendance((prev) => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        status,
      },
    }));
  };

  const handleRemarksChange = (studentId, remarks) => {
    setAttendance((prev) => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        remarks,
      },
    }));
  };

  const handleBulkAction = (status) => {
    const updatedAttendance = {};
    filteredStudents.forEach((student) => {
      updatedAttendance[student.id] = {
        status,
        remarks: attendance[student.id]?.remarks || "",
      };
    });
    setAttendance((prev) => ({ ...prev, ...updatedAttendance }));
    toast.success(`Marked all displayed students as ${status}`);
  };

  const handleSaveAttendance = async () => {
    if (!selectedBatch || !selectedDate) {
      toast.error("Please select batch and date");
      return;
    }

    try {
      setSaving(true);

      const attendancePayload = Object.entries(attendance).map(
        ([studentId, data]) => ({
          studentId: Number(studentId),
          batchId: Number(selectedBatch),
          subjectId: Number(selectedSubject) || 0,
          attendanceDate: new Date(selectedDate).toISOString(),
          status: data.status,
          remarks: data.remarks || "",
        })
      );

      // Filter only for the students currently in our list (to avoid sending stale data)
      const currentStudentIds = new Set(students.map((s) => s.id.toString()));
      const filteredPayload = attendancePayload.filter((item) =>
        currentStudentIds.has(item.studentId.toString())
      );

      await axios.post(
        `${server}/attendance/mark`,
        {
          attendance: filteredPayload,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      toast.success("Attendance saved successfully!");
    } catch (error) {
      console.error("Save error:", error);
      toast.error(
        error?.response?.data?.message || "Failed to save attendance"
      );
    } finally {
      setSaving(false);
    }
  };

  const filteredStudents = students.filter((student) =>
    `${student.firstName} ${student.lastName} ${student.enrollmentNumber}`
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
  );

  const getAttendanceSummary = () => {
    const summary = {
      present: 0,
      absent: 0,
      late: 0,
      half_day: 0,
      leave: 0,
    };

    // Calculate summary only for filtered students or all students?
    // Usually summary is for the whole batch.
    Object.keys(attendance).forEach((studentId) => {
      // Only count if student is in the current 'students' list
      if (students.find((s) => s.id == studentId)) {
        const status = attendance[studentId].status;
        if (summary[status] !== undefined) {
          summary[status]++;
        }
      }
    });

    return summary;
  };

  const summary = getAttendanceSummary();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6 font-sans text-slate-100">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row md:items-center justify-between gap-6"
        >
          <div>
            <h1 className="text-4xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-cyan-400 flex items-center gap-3">
              <UserCheck className="w-10 h-10 text-blue-400" />
              Attendance
            </h1>
            <p className="text-slate-400 mt-2 text-lg">
              Manage daily attendance records efficiently
            </p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => {
                setAttendance({});
                fetchStudents();
              }}
              className="flex items-center gap-2 px-5 py-2.5 bg-slate-800 text-slate-300 rounded-xl hover:bg-slate-700 transition-all border border-slate-700 shadow-lg hover:shadow-xl"
            >
              <RefreshCw className="w-4 h-4" />
              Reset
            </button>
            <button
              onClick={handleSaveAttendance}
              disabled={saving || !selectedBatch}
              className="flex items-center gap-2 px-8 py-2.5 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all font-bold disabled:opacity-50 disabled:cursor-not-allowed transform hover:-translate-y-0.5"
            >
              {saving ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : (
                <Save className="w-5 h-5" />
              )}
              {saving ? "Saving..." : "Save Records"}
            </button>
          </div>
        </motion.div>

        {/* Control Panel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-slate-800/50 backdrop-blur-xl rounded-3xl p-6 border border-slate-700/50 shadow-2xl"
        >
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2">
                <Calendar className="w-4 h-4" /> Date
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                max={new Date().toISOString().split("T")[0]}
                className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-xl text-white focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all outline-none"
              />
            </div>

            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2">
                <Users className="w-4 h-4" /> Batch
              </label>
              <div className="relative">
                <select
                  value={selectedBatch}
                  onChange={(e) => setSelectedBatch(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-xl text-white focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all outline-none appearance-none"
                >
                  <option value="">Select Batch</option>
                  {batches.map((batch) => (
                    <option key={batch.id} value={batch.id}>
                      {batch.batchName} ({batch.batchCode})
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2">
                <BookOpen className="w-4 h-4" /> Subject
              </label>
              <div className="relative">
                <select
                  value={selectedSubject}
                  onChange={(e) => setSelectedSubject(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-xl text-white focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all outline-none appearance-none"
                >
                  <option value="">No Subject (General)</option>
                  {subjects.map((subject) => (
                    <option key={subject.id} value={subject.id}>
                      {subject.subject}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2">
                <Search className="w-4 h-4" /> Search
              </label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Find student..."
                className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-xl text-white focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all outline-none placeholder-slate-500"
              />
            </div>
          </div>
        </motion.div>

        {/* content area */}
        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div
              key="loader"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center justify-center py-20 gap-4"
            >
              <div className="w-16 h-16 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
              <p className="text-slate-400 animate-pulse">
                Loading attendance data...
              </p>
            </motion.div>
          ) : !selectedBatch ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center py-20 bg-slate-800/30 rounded-3xl border border-dashed border-slate-700"
            >
              <div className="w-20 h-20 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl">
                <Users className="w-10 h-10 text-slate-500" />
              </div>
              <h3 className="text-xl font-bold text-slate-300 mb-2">
                No Batch Selected
              </h3>
              <p className="text-slate-500">
                Please select a batch from the controls above to start.
              </p>
            </motion.div>
          ) : (
            <motion.div
              key="content"
              initial="hidden"
              animate="visible"
              variants={containerVariants}
              className="space-y-6"
            >
              {/* Stats & Actions */}
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                <div className="lg:col-span-3 grid grid-cols-2 sm:grid-cols-5 gap-3">
                  {attendanceStatuses.map((status) => (
                    <motion.div
                      key={status.value}
                      whileHover={{ scale: 1.02 }}
                      className={`p-4 rounded-2xl border ${status.bg} ${status.border} backdrop-blur-sm relative overflow-hidden group`}
                    >
                      <div className="relative z-10">
                        <div className="flex items-center gap-2 mb-2 text-xs font-bold uppercase tracking-wider opacity-80">
                          <status.icon className={`w-4 h-4 ${status.color}`} />
                          <span className={status.color}>{status.label}</span>
                        </div>
                        <p className={`text-2xl font-black ${status.color}`}>
                          {summary[status.value]}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <div className="bg-slate-800/50 p-4 rounded-2xl border border-slate-700 flex flex-col justify-center gap-3">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
                    Quick Actions
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => handleBulkAction("present")}
                      className="flex-1 py-2 px-3 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-lg hover:bg-emerald-500/20 transition-all text-xs font-bold flex items-center justify-center gap-2"
                    >
                      <Check className="w-3 h-3" /> All Present
                    </button>
                    <button
                      onClick={() => handleBulkAction("absent")}
                      className="flex-1 py-2 px-3 bg-rose-500/10 text-rose-400 border border-rose-500/20 rounded-lg hover:bg-rose-500/20 transition-all text-xs font-bold flex items-center justify-center gap-2"
                    >
                      <X className="w-3 h-3" /> All Absent
                    </button>
                  </div>
                </div>
              </div>

              {/* Student List */}
              <div className="grid grid-cols-1 gap-4">
                {filteredStudents.length === 0 ? (
                  <div className="text-center py-10">
                    <p className="text-slate-500">
                      No students found matching your search.
                    </p>
                  </div>
                ) : (
                  filteredStudents.map((student) => {
                    const currentStatus = attendance[student.id]?.status;
                    const statusConfig = attendanceStatuses.find(
                      (s) => s.value === currentStatus
                    );

                    return (
                      <motion.div
                        variants={itemVariants}
                        key={student.id}
                        className={`bg-slate-800 rounded-2xl p-4 border transition-all duration-300 ${
                          statusConfig
                            ? `${statusConfig.border} border-l-4`
                            : "border-slate-700 border-l-4 border-l-slate-600"
                        } hover:shadow-lg`}
                      >
                        <div className="flex flex-col md:flex-row items-center gap-6">
                          {/* Student Info */}
                          <div className="flex items-center gap-4 flex-1 w-full md:w-auto">
                            <div
                              className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl font-bold text-white shadow-inner ${
                                statusConfig
                                  ? statusConfig.activeBg
                                  : "bg-slate-700"
                              }`}
                            >
                              {student.firstName[0]}
                            </div>
                            <div className="flex-1">
                              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                                {student.firstName} {student.lastName}
                                {currentStatus === "absent" && (
                                  <UserX className="w-4 h-4 text-rose-400" />
                                )}
                                {currentStatus === "present" && (
                                  <UserCheck className="w-4 h-4 text-emerald-400" />
                                )}
                              </h3>
                              <p className="text-sm text-slate-400 font-mono bg-slate-900/50 px-2 py-0.5 rounded inline-block mt-1">
                                {student.enrollmentNumber}
                              </p>
                            </div>
                          </div>

                          {/* Controls */}
                          <div className="flex flex-col sm:flex-row items-center gap-4 w-full md:w-auto">
                            <div className="flex p-1 bg-slate-900/50 rounded-xl border border-slate-700/50">
                              {attendanceStatuses.map((status) => (
                                <button
                                  key={status.value}
                                  onClick={() =>
                                    handleStatusChange(student.id, status.value)
                                  }
                                  className={`p-2 rounded-lg transition-all relative group ${
                                    currentStatus === status.value
                                      ? `${status.bg} ${status.color} shadow-sm`
                                      : "text-slate-500 hover:text-slate-300 hover:bg-slate-800"
                                  }`}
                                  title={status.label}
                                >
                                  <status.icon className="w-5 h-5" />
                                  {currentStatus === status.value && (
                                    <motion.div
                                      layoutId={`active-indicator-${student.id}`}
                                      className={`absolute inset-0 rounded-lg border ${status.border}`}
                                      transition={{ duration: 0.2 }}
                                    />
                                  )}
                                </button>
                              ))}
                            </div>

                            <div className="w-full sm:w-64">
                              <input
                                type="text"
                                value={attendance[student.id]?.remarks || ""}
                                onChange={(e) =>
                                  handleRemarksChange(
                                    student.id,
                                    e.target.value
                                  )
                                }
                                placeholder="Add remarks..."
                                className="w-full px-4 py-2 bg-slate-900/50 border border-slate-700 rounded-xl text-slate-300 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none placeholder-slate-600"
                              />
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AttendancePage;
